import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, GRADE_BANDS, GRADE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, GraduationCap, Users, School, Building2 } from "lucide-react";
import { toast } from "sonner";

export const RolePicker = ({ role, setRole }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="login-role-picker">
    {[
      { v: "student", label: "Student", icon: GraduationCap },
      { v: "parent", label: "Parent", icon: Users },
      { v: "teacher", label: "Teacher", icon: School },
      { v: "school", label: "School", icon: Building2 },
    ].map((r) => (
      <button
        type="button"
        key={r.v}
        onClick={() => setRole(r.v)}
        data-testid={`role-option-${r.v}`}
        className={`flex flex-col items-center gap-1.5 rounded-xl border py-3 text-sm font-medium transition-colors duration-150 ${
          role === r.v ? "border-[#0F7B7D] bg-[#D7F3F1] text-[#071A22]" : "border-border bg-white hover:bg-secondary"
        }`}
      >
        <r.icon className="h-5 w-5" />
        {r.label}
      </button>
    ))}
  </div>
);

export const GradeSelect = ({ value, onChange, testid = "grade-select" }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className="bg-white h-11" data-testid={testid}>
      <SelectValue placeholder="Choose grade level" />
    </SelectTrigger>
    <SelectContent>
      {GRADE_BANDS.map((g) => (
        <SelectItem key={g} value={g} data-testid={`${testid}-option-${g}`}>{GRADE_LABELS[g]}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 3.3 14.7 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12S6.6 21.7 12 21.7c6.9 0 9.2-4.8 9.2-7.3 0-.5 0-.9-.1-1.3H12z" />
  </svg>
);

export default function AuthPage({ mode }) {
  const [tab, setTab] = useState(mode);
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || "student");
  const [grade, setGrade] = useState("");
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [busy, setBusy] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const nextUrl = params.get("next");

  useEffect(() => setTab(mode), [mode]);
  useEffect(() => {
    if (user && !busy) {
      if (nextUrl && nextUrl.startsWith("/")) navigate(nextUrl, { replace: true });
      else navigate(user.role ? "/dashboard" : "/onboarding", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const after = nextUrl && nextUrl.startsWith("/") ? nextUrl : "/dashboard";
    const redirectUrl = window.location.origin + after;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tab === "signup" && role === "student" && !grade) {
      toast.error("Please choose a grade level");
      return;
    }
    setBusy(true);
    try {
      const { data } =
        tab === "login"
          ? await api.post("/auth/login", { email: form.email, password: form.password })
          : await api.post("/auth/register", { ...form, role, grade_band: role === "student" ? grade : null, school_code: role === "teacher" ? params.get("school") : null });
      signIn(data.token, data.user);
      toast.success(tab === "login" ? `Welcome back, ${data.user.name.split(" ")[0]}!` : "Your account is ready!");
      if (nextUrl && nextUrl.startsWith("/")) navigate(nextUrl);
      else navigate(tab === "signup" && data.user.role !== "student" ? "/billing" : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBFAF4]">
      <div className="hidden lg:flex flex-col justify-between p-12 hero-wash border-r border-border">
        <Brand />
        <div>
          <h2 className="text-4xl font-display font-semibold tracking-tight max-w-md leading-tight">Learning that meets every student where they are.</h2>
          <ul className="mt-8 space-y-3 text-[#123645]">
            <li>• Courses from Pre-K through College</li>
            <li>• A patient AI tutor that guides, step by step</li>
            <li>• Progress parents and teachers can trust</li>
          </ul>
        </div>
        <img
          src="https://images.pexels.com/photos/39191130/pexels-photo-39191130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Parent and child learning at home"
          className="rounded-[18px] h-56 w-full object-cover border border-border"
        />
      </div>

      <div className="flex flex-col items-center justify-center px-4 py-10">
        <div className="lg:hidden mb-8"><Brand /></div>
        <div className="w-full max-w-md bg-white border border-border rounded-[18px] p-6 sm:p-8 shadow-[var(--shadow-sm)] fade-up">
          <Tabs value={tab} onValueChange={(v) => navigate(v === "login" ? "/login" : "/signup")}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup">Create account</TabsTrigger>
            </TabsList>
          </Tabs>

          <Button type="button" variant="outline" className="w-full h-11 bg-white gap-2" onClick={google} data-testid="login-google-button">
            <GoogleIcon /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" /><span className="text-xs text-muted-foreground">or with email</span><Separator className="flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "signup" && (
              <>
                <div className="space-y-2">
                  <Label>I am a...</Label>
                  <RolePicker role={role} setRole={setRole} />
                </div>
                {role === "teacher" && params.get("school") && (
                  <div className="text-xs bg-[#D7F3F1] text-[#0B5B5B] rounded-lg px-3 py-2" data-testid="signup-school-note">You'll join your school (code {params.get("school")}) automatically.</div>
                )}
                {role === "student" && (
                  <div className="space-y-2">
                    <Label>Grade level</Label>
                    <GradeSelect value={grade} onChange={setGrade} testid="signup-grade-select" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="signup-name-input" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={tab === "signup" ? 6 : 1} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" data-testid="login-password-input" />
              {tab === "signup" && <p className="text-xs text-muted-foreground">At least 6 characters.</p>}
            </div>
            <Button type="submit" className="w-full h-11" disabled={busy} data-testid={tab === "login" ? "login-form-submit-button" : "signup-submit-button"}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {tab === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-6 text-center">
            School admin accounts are set up by your school. <Link to="/" className="underline">Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
